import { useEffect } from 'react';
import { BookOpen, Scale, Users, MapPin, Crown, Shield } from 'lucide-react';

const TangataWhenuaRights = () => {
  useEffect(() => {
    document.title = 'Tangata Whenua Rights | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  const rights = [
    {
      icon: <Scale className="h-6 w-6 text-ocean-teal" />,
      title: "Customary Rights",
      description: "Traditional rights to land, resources, and cultural practices that have been maintained since time immemorial."
    },
    {
      icon: <MapPin className="h-6 w-6 text-ocean-teal" />,
      title: "Land Rights",
      description: "Rights to ancestral lands and territories, including access and management of traditional resources."
    },
    {
      icon: <Users className="h-6 w-6 text-ocean-teal" />,
      title: "Collective Rights",
      description: "Rights exercised collectively by iwi, hapū, and whānau, including decision-making processes."
    },
    {
      icon: <Crown className="h-6 w-6 text-ocean-teal" />,
      title: "Sovereignty Rights",
      description: "Rights of self-governance and self-determination within traditional territories."
    },
    {
      icon: <Shield className="h-6 w-6 text-ocean-teal" />,
      title: "Cultural Protection",
      description: "Rights to protect and maintain cultural practices, language, and spiritual beliefs."
    },
    {
      icon: <BookOpen className="h-6 w-6 text-ocean-teal" />,
      title: "Knowledge Rights",
      description: "Rights to traditional knowledge systems and intellectual property protection."
    }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-ocean-teal to-ocean-deep text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Tangata Whenua Rights
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Understanding and exercising your indigenous rights as tangata whenua of Aotearoa
          </p>
        </div>
      </section>

      {/* Rights Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-header">Your Indigenous Rights</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              As tangata whenua, you have inherent rights that stem from your ancestral connection 
              to this land. These rights flow from your whakapapa and connection to whenua.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rights.map((right, index) => (
              <div key={index} className="card">
                <div className="flex items-center mb-4">
                  {right.icon}
                  <h3 className="text-xl font-semibold ml-3 text-gray-800">
                    {right.title}
                  </h3>
                </div>
                <p className="text-gray-600">{right.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Framework */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Cultural Recognition</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="cultural-border">
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">He Whakaputanga</h3>
              <p className="text-gray-600 mb-4">
                The Declaration of Independence (1835) affirmed the sovereign rights of iwi and hapū 
                over their ancestral territories and established the foundation for self-governance.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Recognition of rangatira authority</li>
                <li>Protection of whenua and taonga</li>
                <li>Mana motuhake (independent authority)</li>
                <li>Cultural and spiritual protection</li>
              </ul>
            </div>

            <div className="cultural-border">
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Tikanga Māori</h3>
              <p className="text-gray-600 mb-4">
                Traditional Māori customs and values provide the foundation for exercising 
                customary rights within iwi and hapū structures.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Whakapapa (genealogical connections)</li>
                <li>Manaakitanga (hospitality and care)</li>
                <li>Kaitiakitanga (guardianship)</li>
                <li>Whakawhanaungatanga (relationship building)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How to Exercise Rights */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Exercising Your Rights</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="card">
                <h3 className="text-xl font-semibold mb-4 text-ocean-teal">Cultural Pathways</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Hui with iwi and hapū</li>
                  <li>• Participation in marae activities</li>
                  <li>• Learning whakapapa and tikanga</li>
                  <li>• Connecting with kaumātua</li>
                  <li>• Engaging in cultural practices</li>
                </ul>
              </div>

              <div className="card">
                <h3 className="text-xl font-semibold mb-4 text-ocean-teal">Community Engagement</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Strengthening whānau connections</li>
                  <li>• Participating in cultural events</li>
                  <li>• Supporting hapū initiatives</li>
                  <li>• Preserving traditional knowledge</li>
                  <li>• Building iwi networks</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 card bg-ocean-blue bg-opacity-10">
              <h3 className="text-xl font-semibold mb-4 text-ocean-deep">Important Note</h3>
              <p className="text-gray-700">
                This information is for cultural education and discussion. For guidance on exercising 
                customary rights, engage with your iwi, hapū, and kaumātua who hold traditional knowledge.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TangataWhenuaRights;
